import { useQuery } from '@tanstack/react-query';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { detectPhone } from '../../phone-search/lib/detectPhone';
import { titularAddress } from '../lib/titularAddress';

const FIVE_MINUTES = 5 * 60 * 1000;

// The stored address of the «Entrega»'s titular, for the address-mismatch warning. Null whenever
// there is nothing to compare: no titular, a phone that is not one, the read still in flight, the
// read failed, or no address on file.
//
// It reads the SAME key as useAccount (`['phone-search', <phone>]`), so a detail opened from a
// «Usuario» tab finds the answer already warm. The error policy is the opposite, on purpose:
// useAccount throws to the shell's boundary because its card has nothing else to show, while here a
// failure only means no warning — and it must never take the detail down with it.
//
// The key is minted by detectPhone, never from the detail's raw phone: the detail carries the phone
// as stored, without its value object, and the tab key is detectPhone's output. Minted any other
// way, the key misses the cache AND may send a phone the endpoint cannot resolve — no hits, no
// address, and the warning fails silently.
export function useTitularAddress(
  recipient: { id: number | null; phone: string | null } | null,
  searchUsersByPhone: (phone: string) => Promise<UserSearchHit[]>,
): string | null {
  const phone = detectPhone(recipient?.phone ?? '');
  const { data } = useQuery({
    queryKey: ['phone-search', phone],
    queryFn: () => searchUsersByPhone(phone ?? ''),
    enabled: phone !== null,
    staleTime: FIVE_MINUTES,
    throwOnError: false,
  });
  return data === undefined ? null : titularAddress(data, recipient?.id ?? null);
}
